import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronRight,
  Calculator,
  AlertTriangle,
  Info,
  XCircle,
  ExternalLink,
  BookOpen,
  Headphones,
} from "lucide-react";
import { PAYROLL_ERRORS } from "./PayrollHelpPage";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";
import "../../styles/supportCenter.css";

const SEVERITY_STYLE = {
  Error: { bg: "rgba(239,68,68,0.08)", color: "#dc2626", border: "rgba(239,68,68,0.18)" },
  Warning: { bg: "rgba(245,158,11,0.08)", color: "#b45309", border: "rgba(245,158,11,0.18)" },
  Info: { bg: "rgba(59,130,246,0.08)", color: "#1d4ed8", border: "rgba(59,130,246,0.18)" },
};

const SeverityIcon = ({ severity }) => {
  if (severity === "Error") return <XCircle size={16} color="#dc2626" />;
  if (severity === "Warning") return <AlertTriangle size={16} color="#b45309" />;
  return <Info size={16} color="#1d4ed8" />;
};

const QUICK_ACTIONS = [
  { label: "Process Payroll", path: "/payroll-process", desc: "Run or manage payroll processing" },
  { label: "Payroll Runs", path: "/payroll-runs", desc: "View payroll run history" },
  { label: "Employee Salary Structure", path: "/employeesalarystructure", desc: "Assign salary packages" },
  { label: "Salary Components", path: "/salary-v2/components", desc: "Manage salary components" },
  { label: "Statutory Settings", path: "/settings/statutory/pf", desc: "Configure PF, ESI, PT" },
  { label: "Attendance Records", path: "/attendance/records", desc: "Review attendance data" },
];

export default function PayrollSupportPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openError, setOpenError] = useState(null);
  const resultsRef = useRef(null);

  const q = search.trim().toLowerCase();

  const filteredErrors = useMemo(() => {
    if (!q) return PAYROLL_ERRORS;
    return PAYROLL_ERRORS.filter(
      (e) =>
        e.code.toLowerCase().includes(q) ||
        e.cause.toLowerCase().includes(q) ||
        e.fix.toLowerCase().includes(q)
    );
  }, [q]);

  return (
    <div className="brix-page">
      {/* ── Header ── */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-blue">
              <Calculator size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Payroll Calculation Support</h1>
              <p className="hs-hero-sub">
                Troubleshoot payroll processing and calculation issues.
              </p>
            </div>
          </div>

          <form
            className="hs-search-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              if (q && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search payroll issues — e.g. salary not assigned, LOP, reprocess..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div ref={resultsRef}>
        {/* ── Quick Actions ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Quick Actions</h2>
            <p className="hs-section-sub">Jump to the relevant page to fix your issue</p>
          </div>
          <div className="sc-issues-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {QUICK_ACTIONS.map((link) => (
              <div
                key={link.path}
                className="sc-issue-card"
                role="button"
                tabIndex={0}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(link.path)}
                onKeyDown={(e) => e.key === "Enter" && navigate(link.path)}
              >
                <div className="sc-issue-name" style={{ fontSize: "0.88rem" }}>{link.label}</div>
                <div className="sc-issue-desc" style={{ fontSize: "0.78rem" }}>{link.desc}</div>
                <div className="sc-issue-link">
                  Open <ExternalLink size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Payroll Issues & Solutions (main section) ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Payroll Issues &amp; Solutions</h2>
            <p className="hs-section-sub">
              {PAYROLL_ERRORS.length} known issues with step-by-step fixes
            </p>
          </div>

          {q && filteredErrors.length === 0 && (
            <div className="sc-no-results">
              No payroll issues match &ldquo;<strong>{search}</strong>&rdquo;. Try a different keyword.
            </div>
          )}

          <div className="sc-accordion-list">
            {filteredErrors.map((err, idx) => {
              const isOpen = openError === idx;
              const sev = SEVERITY_STYLE[err.severity] || SEVERITY_STYLE.Error;
              return (
                <div key={err.code} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenError(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <SeverityIcon severity={err.severity} />
                      <span>{err.code}</span>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 4,
                          background: sev.bg,
                          color: sev.color,
                          border: `1px solid ${sev.border}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {err.severity}
                      </span>
                    </span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">
                          <div style={{ marginBottom: 10 }}>
                            <strong style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Why this happens
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{err.cause}</p>
                          </div>
                          <div>
                            <strong style={{ color: "#15803d", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              How to fix
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{err.fix}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Need detailed payroll information? ── */}
        <div className="sc-col-card" style={{ marginBottom: 28 }}>
          <div className="sc-info-row">
            <div className="sc-col-header-icon sc-icon-blue">
              <BookOpen size={18} strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="hs-section-title">Need detailed payroll information?</h2>
              <p className="sc-info-row-desc">
                Visit the Payroll Help Guide for detailed documentation, FAQs and step-by-step payroll instructions.
              </p>
            </div>
            <button
              type="button"
              className="sc-info-row-btn"
              onClick={() => navigate("/help/payroll")}
            >
              Open Payroll Help Guide
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* ── Still facing an issue? ── */}
        <div className="sc-contact-card" style={{ marginBottom: 28 }}>
          <div className="sc-contact-inner sc-contact-row">
            <div className="sc-contact-icon">
              <Headphones size={24} strokeWidth={1.5} />
            </div>
            <div className="sc-contact-text">
              <h2 className="sc-contact-title">Still facing an issue?</h2>
              <p className="sc-contact-sub">
                If the available troubleshooting steps don't resolve the problem, contact our support team for further assistance.
              </p>
            </div>
            <button
              type="button"
              className="sc-contact-btn"
              onClick={() => navigate("/support")}
            >
              <Headphones size={15} />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
